// AstroPet, pet natal chart + owner-pet compatibility
// Route: /astro-pet

import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Sparkles, Share2, ChevronLeft, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { safeInvoke } from '../lib/safeInvoke';
import { TextSkeleton } from '../components/shared/TextSkeleton';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface PetChart {
  id:                  string;
  pet_name:            string;
  pet_type:            string;
  pet_breed:           string | null;
  birth_date:          string;
  birth_time:          string | null;
  sun_sign:            string | null;
  moon_sign:           string | null;
  dominant_element:    string | null;
  personality_summary: string | null;
  energy_type:         string | null;
  dominant_trait:      string | null;
  sun_meaning:         string | null;
  moon_meaning:        string | null;
  communication_style: string | null;
}

interface PetCompat {
  compatibility_score:  number;
  relationship_dynamic: string;
  strengths:            string[];
  challenges:           string[];
  bonding_tips:         string;
  training_insights:    string;
}

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

const PET_TYPES = [
  { value: 'cachorro', label: '🐕 Cachorro' },
  { value: 'gato',     label: '🐈 Gato' },
  { value: 'coelho',   label: '🐇 Coelho' },
  { value: 'peixe',    label: '🐠 Peixe' },
  { value: 'pássaro',  label: '🦜 Pássaro' },
  { value: 'hamster',  label: '🐹 Hamster' },
  { value: 'outro',    label: '🐾 Outro' },
];

const PET_EMOJI: Record<string, string> = {
  cachorro: '🐕', gato: '🐈', coelho: '🐇',
  peixe: '🐠', pássaro: '🦜', hamster: '🐹', outro: '🐾',
};

const ELEMENT_COLOR: Record<string, string> = {
  'Fogo':  '#f97316',
  'Terra': '#84cc16',
  'Ar':    '#60a5fa',
  'Água':  '#38bdf8',
};

const ENERGY_COLOR: Record<string, string> = {
  'alta':  '#4ade80',
  'média': '#fbbf24',
  'baixa': '#94a3b8',
};

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function formatDateBR(d: string): string {
  const [y, m, day] = d.split('-');
  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
}

function whatsappShare(text: string) {
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
}

function scoreColor(score: number): string {
  if (score >= 80) return '#4ade80';
  if (score >= 60) return '#fbbf24';
  if (score >= 40) return '#fb923c';
  return '#f87171';
}

// ─────────────────────────────────────────
// Score ring
// ─────────────────────────────────────────

function ScoreRing({ score, label }: { score: number; label: string }) {
  const r = 44, circ = 2 * Math.PI * r;
  const color = scoreColor(score);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="var(--border-subtle)" strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - score / 100)}
          strokeLinecap="round" transform="rotate(-90 55 55)"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
        <text x="55" y="50" textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--text-primary)" fontFamily="sans-serif">{score}</text>
        <text x="55" y="65" textAnchor="middle" fontSize="9" fill="var(--text-muted)" fontFamily="sans-serif">/ 100</text>
      </svg>
      <p style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600, textAlign: 'center' }}>{label}</p>
    </div>
  );
}

// ─────────────────────────────────────────
// Pet card (list view)
// ─────────────────────────────────────────

function PetCard({
  pet,
  compat,
  onClick,
  onDelete,
}: {
  pet:      PetChart;
  compat:   PetCompat | null;
  onClick:  () => void;
  onDelete: () => void;
}) {
  const emoji = PET_EMOJI[pet.pet_type] ?? '🐾';
  const color = pet.dominant_element ? ELEMENT_COLOR[pet.dominant_element] : 'var(--color-gold)';

  return (
    <div style={{
      borderRadius: 'var(--radius-lg)',
      border: `1px solid ${color}33`,
      background: 'var(--surface-card)',
      padding: '1rem 1.25rem',
      display: 'flex', alignItems: 'center', gap: '1rem',
      cursor: 'pointer',
      marginBottom: '0.75rem',
    }} onClick={onClick}>
      <div style={{
        width: '52px', height: '52px', borderRadius: '50%',
        background: color + '22',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.75rem', flexShrink: 0,
      }}>
        {emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)', marginBottom: '0.15rem' }}>
          {pet.pet_name}
          {pet.pet_breed && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}> · {pet.pet_breed}</span>}
        </p>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
          {pet.sun_sign ? `☀️ ${pet.sun_sign}` : formatDateBR(pet.birth_date)}
          {pet.moon_sign ? ` · 🌙 ${pet.moon_sign}` : ''}
        </p>
      </div>
      {compat && (
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 800, color: scoreColor(compat.compatibility_score) }}>
            {compat.compatibility_score}%
          </span>
          <p style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>sintonia</p>
        </div>
      )}
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0, padding: '0.25rem' }}
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────
// Pet detail view
// ─────────────────────────────────────────

function PetDetail({
  pet,
  compat,
  onBack,
  onRegenerate,
  regenerating,
}: {
  pet:         PetChart;
  compat:      PetCompat | null;
  onBack:      () => void;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const [tab, setTab] = useState<'chart' | 'compat'>('chart');
  const emoji   = PET_EMOJI[pet.pet_type] ?? '🐾';
  const elColor = pet.dominant_element ? ELEMENT_COLOR[pet.dominant_element] : 'var(--color-gold)';
  const enColor = pet.energy_type      ? ENERGY_COLOR[pet.energy_type]       : 'var(--text-muted)';

  const shareText = `Meu ${pet.pet_type} ${pet.pet_name} é ${pet.sun_sign ? `um ${pet.sun_sign}` : 'especial'} ✦ Descubra o mapa natal do seu pet no Professya!`;

  return (
    <div>
      {/* Back */}
      <button
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1.25rem', padding: 0 }}
      >
        <ChevronLeft size={16} /> Meus pets
      </button>

      {/* Pet header */}
      <div style={{
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-card)',
        border: `1px solid ${elColor}33`,
        padding: '1.5rem',
        marginBottom: '1rem',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>{emoji}</div>
        <h2 style={{ fontSize: 'clamp(1.25rem,4vw,1.625rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {pet.pet_name}
        </h2>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          {pet.pet_type}{pet.pet_breed ? ` · ${pet.pet_breed}` : ''} · {formatDateBR(pet.birth_date)}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {pet.sun_sign && (
            <span style={{ padding: '0.25rem 0.65rem', borderRadius: '999px', background: '#fbbf2422', color: '#fbbf24', fontSize: 'var(--text-caption)', fontWeight: 600 }}>
              ☀️ {pet.sun_sign}
            </span>
          )}
          {pet.moon_sign && (
            <span style={{ padding: '0.25rem 0.65rem', borderRadius: '999px', background: '#60a5fa22', color: '#60a5fa', fontSize: 'var(--text-caption)', fontWeight: 600 }}>
              🌙 {pet.moon_sign}
            </span>
          )}
          {pet.dominant_element && (
            <span style={{ padding: '0.25rem 0.65rem', borderRadius: '999px', background: elColor + '22', color: elColor, fontSize: 'var(--text-caption)', fontWeight: 600 }}>
              {pet.dominant_element}
            </span>
          )}
          {pet.energy_type && (
            <span style={{ padding: '0.25rem 0.65rem', borderRadius: '999px', background: enColor + '22', color: enColor, fontSize: 'var(--text-caption)' }}>
              Energia {pet.energy_type}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button className={tab === 'chart' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={() => setTab('chart')} style={{ flex: 1 }}>
          🌟 Mapa do Pet
        </button>
        <button className={tab === 'compat' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={() => setTab('compat')} style={{ flex: 1 }}>
          💞 Sintonia
        </button>
      </div>

      {/* Chart tab */}
      {tab === 'chart' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {pet.personality_summary && (
            <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
              <h4 style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
                Personalidade astrológica
              </h4>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {pet.personality_summary}
              </p>
            </div>
          )}

          {pet.dominant_trait && (
            <div style={{
              background: elColor + '11',
              borderRadius: 'var(--radius-lg)',
              padding: '1rem 1.25rem',
              border: `1px solid ${elColor}33`,
            }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Traço dominante</p>
              <p style={{ fontWeight: 600, color: elColor, fontSize: 'var(--text-label)' }}>{pet.dominant_trait}</p>
            </div>
          )}

          {pet.sun_meaning && (
            <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
              <h4 style={{ fontWeight: 600, color: '#fbbf24', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
                ☀️ Sol em {pet.sun_sign}
              </h4>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{pet.sun_meaning}</p>
            </div>
          )}

          {pet.moon_meaning && (
            <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
              <h4 style={{ fontWeight: 600, color: '#60a5fa', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
                🌙 Lua, Instintos e Emoções
              </h4>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{pet.moon_meaning}</p>
            </div>
          )}

          {pet.communication_style && (
            <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
              <h4 style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
                💬 Como {pet.pet_name} se comunica
              </h4>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{pet.communication_style}</p>
            </div>
          )}

          {/* Share button */}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => whatsappShare(shareText)}
            style={{ alignSelf: 'flex-start', gap: '0.4rem' }}
          >
            <Share2 size={14} />
            Compartilhar no WhatsApp
          </button>
        </div>
      )}

      {/* Compatibility tab */}
      {tab === 'compat' && compat && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
            <ScoreRing
              score={compat.compatibility_score}
              label={`Você e ${pet.pet_name}`}
            />
          </div>

          <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
            <h4 style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
              Dinâmica da relação
            </h4>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {compat.relationship_dynamic}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={{ background: 'rgba(74,222,128,0.06)', borderRadius: 'var(--radius-lg)', padding: '1rem', border: '1px solid rgba(74,222,128,0.2)' }}>
              <h4 style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: '#4ade80', marginBottom: '0.5rem' }}>Forças</h4>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {compat.strengths?.map((s, i) => (
                  <li key={i} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>✓ {s}</li>
                ))}
              </ul>
            </div>
            <div style={{ background: 'rgba(251,146,60,0.06)', borderRadius: 'var(--radius-lg)', padding: '1rem', border: '1px solid rgba(251,146,60,0.2)' }}>
              <h4 style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: '#fb923c', marginBottom: '0.5rem' }}>Desafios</h4>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {compat.challenges?.map((c, i) => (
                  <li key={i} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>! {c}</li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
            <h4 style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
              💞 Como fortalecer o vínculo
            </h4>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {compat.bonding_tips}
            </p>
          </div>

          <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', border: '1px solid var(--border-subtle)' }}>
            <h4 style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
              🎓 Como educar {pet.pet_name}
            </h4>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {compat.training_insights}
            </p>
          </div>
        </div>
      )}

      {tab === 'compat' && !compat && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <p style={{ marginBottom: '1rem' }}>Análise de compatibilidade não disponível.</p>
          <button className="btn btn-primary btn-sm" onClick={onRegenerate} disabled={regenerating}>
            {regenerating ? 'Gerando...' : 'Gerar análise'}
          </button>
        </div>
      )}

      <button
        className="btn btn-secondary btn-sm"
        onClick={onRegenerate}
        disabled={regenerating}
        style={{ marginTop: '1.5rem' }}
      >
        {regenerating ? <><Sparkles size={14} className="animate-pulse" /> Regenerando...</> : 'Regenerar análise'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────
// Add pet form
// ─────────────────────────────────────────

interface PetForm {
  pet_name:   string;
  pet_type:   string;
  pet_breed:  string;
  birth_date: string;
  birth_time: string;
  birth_city: string;
}

type BirthMode = 'exact' | 'month_year' | 'age';

const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const NOMINATIM_PROXY = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/nominatim-proxy`;

const EMPTY_FORM: PetForm = { pet_name: '', pet_type: 'cachorro', pet_breed: '', birth_date: '', birth_time: '', birth_city: '' };

function CityAutocomplete({ value, onChange }: { value: string; onChange: (city: string) => void }) {
  const [query,       setQuery]       = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open,        setOpen]        = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetch_suggestions = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `${NOMINATIM_PROXY}?mode=search&q=${encodeURIComponent(q)}&limit=7`;
        const res  = await fetch(url);
        const data = await res.json() as Array<{ address: Record<string, string>; display_name: string }>;
        const labels = data.map(r => {
          const addr = r.address ?? {};
          const city = addr.city ?? addr.town ?? addr.municipality ?? addr.village ?? addr.county ?? '';
          const state = addr.state ?? addr.region ?? '';
          const country = addr.country_code?.toUpperCase() ?? '';
          return [city, state, country].filter(Boolean).join(', ');
        }).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 6);
        setSuggestions(labels);
        setOpen(labels.length > 0);
      } catch { setSuggestions([]); setOpen(false); }
    }, 350);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        placeholder="ex: São Paulo, SP"
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); fetch_suggestions(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          maxHeight: '200px', overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              style={{
                width: '100%', textAlign: 'left', padding: '0.6rem 0.9rem',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 'var(--text-caption)', color: 'var(--text-primary)',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-subtle)' : 'none',
              }}
              onMouseDown={() => { setQuery(s); onChange(s); setOpen(false); }}
            >
              📍 {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddPetForm({
  onSubmit,
  submitting,
  onCancel,
}: {
  onSubmit:   (form: PetForm) => void;
  submitting: boolean;
  onCancel?:  () => void;
}) {
  const { profile }         = useAuth();
  const [form, setForm]     = useState<PetForm>(EMPTY_FORM);
  const [birthMode, setBirthMode] = useState<BirthMode>('exact');
  const [birthMonth, setBirthMonth] = useState('1');
  const [birthYear,  setBirthYear]  = useState(String(new Date().getFullYear() - 3));
  const [petAge,     setPetAge]     = useState('');

  const update = (field: keyof PetForm, value: string) => setForm(f => ({ ...f, [field]: value }));

  const tutorName = profile?.full_name?.split(' ')[0];
  const tutorSign = profile?.sun_sign;

  function getBirthDate(): string {
    if (birthMode === 'exact') return form.birth_date;
    if (birthMode === 'month_year') {
      const m = String(birthMonth).padStart(2, '0');
      return `${birthYear}-${m}-15`;
    }
    if (birthMode === 'age') {
      const age = parseInt(petAge, 10);
      if (!age || age <= 0) return '';
      const year = new Date().getFullYear() - age;
      return `${year}-06-15`;
    }
    return '';
  }

  function isValid(): boolean {
    if (!(form.pet_name ?? '').trim()) return false;
    if (birthMode === 'exact') return !!form.birth_date;
    if (birthMode === 'month_year') return !!birthYear && parseInt(birthYear, 10) > 1990;
    if (birthMode === 'age') return !!petAge && parseInt(petAge, 10) > 0;
    return false;
  }

  function handleSubmit() {
    const bd = getBirthDate();
    if (!bd) return;
    onSubmit({ ...form, birth_date: bd });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Tutor data confirmation */}
      {tutorName && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius-lg)',
          background: 'rgba(31,78,95,0.07)',
          border: '1px solid rgba(31,78,95,0.2)',
          display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}>
          <span style={{ fontSize: '1.5rem' }}>🧑</span>
          <div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.1rem', fontSize: 'var(--text-label)' }}>
              Tutor: {tutorName}
              {tutorSign && <span style={{ marginLeft: '0.4rem', color: 'var(--petrol)', fontWeight: 400 }}>({tutorSign})</span>}
            </p>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 'var(--text-caption)' }}>
              A compatibilidade será calculada com seus dados
            </p>
          </div>
        </div>
      )}

      {/* Intro */}
      <div style={{
        padding: '1rem 1.25rem',
        borderRadius: 'var(--radius-lg)',
        background: 'rgba(245,200,66,0.06)',
        border: '1px solid rgba(245,200,66,0.2)',
        fontSize: 'var(--text-caption)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
      }}>
        🐾 A astrologia pode ser aplicada a qualquer ser nascido em um momento específico.
        Descubra a personalidade astrológica do seu pet e a sintonia entre vocês.
      </div>

      {/* Pet type */}
      <div>
        <label style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '0.6rem' }}>
          Tipo de pet
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {PET_TYPES.map(pt => (
            <button
              key={pt.value}
              type="button"
              onClick={() => update('pet_type', pt.value)}
              style={{
                padding: '0.45rem 0.9rem',
                borderRadius: '999px',
                border: form.pet_type === pt.value ? '2px solid var(--color-gold)' : '1px solid var(--border-subtle)',
                background: form.pet_type === pt.value ? 'rgba(245,200,66,0.12)' : 'var(--surface-card)',
                color: form.pet_type === pt.value ? 'var(--color-gold)' : 'var(--text-secondary)',
                fontSize: 'var(--text-caption)',
                fontWeight: form.pet_type === pt.value ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {pt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div className="form-group">
        <label className="form-label">Nome do pet *</label>
        <input type="text" className="form-input" placeholder="ex: Luna, Thor, Bolinha"
          value={form.pet_name} onChange={e => update('pet_name', e.target.value)} />
      </div>

      {/* Breed */}
      <div className="form-group">
        <label className="form-label">Raça <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
        <input type="text" className="form-input" placeholder="ex: Golden Retriever, Siamês"
          value={form.pet_breed} onChange={e => update('pet_breed', e.target.value)} />
      </div>

      {/* Birth date mode selector */}
      <div>
        <label style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '0.6rem' }}>
          Nascimento *
        </label>
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          {([
            { key: 'exact',      label: '📅 Data exata' },
            { key: 'month_year', label: '📆 Mês e ano' },
            { key: 'age',        label: '🎂 Só sei a idade' },
          ] as { key: BirthMode; label: string }[]).map(m => (
            <button
              key={m.key}
              type="button"
              onClick={() => setBirthMode(m.key)}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: '999px',
                border: birthMode === m.key ? '2px solid var(--color-gold)' : '1px solid var(--border-subtle)',
                background: birthMode === m.key ? 'rgba(245,200,66,0.12)' : 'var(--surface-card)',
                color: birthMode === m.key ? 'var(--color-gold)' : 'var(--text-secondary)',
                fontSize: 'var(--text-caption)',
                fontWeight: birthMode === m.key ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {birthMode === 'exact' && (
          <input type="date" className="form-input"
            value={form.birth_date} onChange={e => update('birth_date', e.target.value)} />
        )}

        {birthMode === 'month_year' && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select className="form-input" value={birthMonth} onChange={e => setBirthMonth(e.target.value)} style={{ flex: 2 }}>
              {MONTHS_PT.map((m, i) => <option key={i + 1} value={String(i + 1)}>{m}</option>)}
            </select>
            <input type="number" className="form-input" placeholder="Ano" min="1990" max={new Date().getFullYear()}
              value={birthYear} onChange={e => setBirthYear(e.target.value)} style={{ flex: 1 }} />
          </div>
        )}

        {birthMode === 'age' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input type="number" className="form-input" placeholder="Idade em anos" min="0" max="30"
              value={petAge} onChange={e => setPetAge(e.target.value)} style={{ width: '130px' }} />
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
              {petAge && parseInt(petAge, 10) > 0 ? `≈ nasceu em ${new Date().getFullYear() - parseInt(petAge, 10)}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Birth time */}
      <div className="form-group">
        <label className="form-label">Hora de nascimento <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
        <input type="time" className="form-input"
          value={form.birth_time} onChange={e => update('birth_time', e.target.value)} />
      </div>

      {/* Birth city with autocomplete */}
      <div className="form-group">
        <label className="form-label">Cidade de nascimento <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
        <CityAutocomplete value={form.birth_city} onChange={city => update('birth_city', city)} />
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {onCancel && (
          <button className="btn btn-secondary" onClick={onCancel} style={{ flex: 1 }}>
            Cancelar
          </button>
        )}
        <button
          className="btn btn-primary"
          disabled={!isValid() || submitting}
          onClick={handleSubmit}
          style={{ flex: 2, gap: '0.4rem', justifyContent: 'center' }}
        >
          {submitting
            ? <><Sparkles size={16} className="animate-pulse" /> Calculando mapa...</>
            : <>✨ Calcular mapa do meu pet</>}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Main page
// ─────────────────────────────────────────

export function AstroPet() {
  const { profile } = useAuth();

  const [pets,       setPets]       = useState<PetChart[]>([]);
  const [compats,    setCompats]    = useState<Record<string, PetCompat>>({});
  const [selected,   setSelected]   = useState<PetChart | null>(null);
  const [adding,     setAdding]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // Load pets and their compatibility analyses
  useEffect(() => {
    if (!profile?.id) return;
    setLoading(true);

    Promise.all([
      supabase.from('pet_natal_charts')
        .select('*')
        .eq('owner_id', profile.id)
        .order('created_at', { ascending: false }),
      supabase.from('pet_compatibility_analyses')
        .select('*')
        .eq('owner_id', profile.id),
    ]).then(([petsRes, compatsRes]) => {
      if (petsRes.data) setPets(petsRes.data as PetChart[]);
      if (compatsRes.data) {
        const map: Record<string, PetCompat> = {};
        for (const c of compatsRes.data) {
          map[c.pet_id] = {
            compatibility_score:  c.compatibility_score,
            relationship_dynamic: c.relationship_dynamic,
            strengths:            c.strengths ?? [],
            challenges:           c.challenges ?? [],
            bonding_tips:         c.bonding_tips,
            training_insights:    c.training_insights,
          };
        }
        setCompats(map);
      }
      setLoading(false);
    });
  }, [profile?.id]);

  const handleSubmit = useCallback(async (form: PetForm) => {
    if (!profile?.id) return;
    setSubmitting(true);
    setError(null);

    const result = await safeInvoke<{ pet: PetChart; compatibility: PetCompat }>(
      'astro-pet',
      {
        body:     { ...form, pet_breed: form.pet_breed || undefined, birth_time: form.birth_time || undefined, birth_city: form.birth_city || undefined },
        timeout:  45_000,
        fallback: null as unknown as { pet: PetChart; compatibility: PetCompat },
      },
    );

    if (result && !result.fromFallback && result.data?.pet) {
      const { pet, compatibility } = result.data;
      setPets(prev => [pet, ...prev.filter(p => p.id !== pet.id)]);
      if (compatibility) setCompats(prev => ({ ...prev, [pet.id]: compatibility }));
      setAdding(false);
      setSelected(pet);
    } else {
      setError('Não foi possível calcular o mapa. Tente novamente.');
    }
    setSubmitting(false);
  }, [profile?.id]);

  const handleRegenerate = useCallback(async () => {
    if (!selected || !profile?.id) return;
    setRegenerating(true);
    setError(null);

    const result = await safeInvoke<{ pet: PetChart; compatibility: PetCompat }>(
      'astro-pet',
      {
        body:     { pet_id: selected.id, pet_name: selected.pet_name, pet_type: selected.pet_type, birth_date: selected.birth_date, regenerate: true },
        timeout:  45_000,
        fallback: null as unknown as { pet: PetChart; compatibility: PetCompat },
      },
    );

    if (result && !result.fromFallback && result.data?.pet) {
      const { pet, compatibility } = result.data;
      setPets(prev => prev.map(p => p.id === pet.id ? pet : p));
      setSelected(pet);
      if (compatibility) setCompats(prev => ({ ...prev, [pet.id]: compatibility }));
    } else {
      setError('Não foi possível regenerar a análise.');
    }
    setRegenerating(false);
  }, [selected, profile?.id]);

  const handleDelete = useCallback(async (petId: string) => {
    if (!confirm('Remover este pet?')) return;
    await supabase.from('pet_natal_charts').delete().eq('id', petId).eq('owner_id', profile?.id ?? '');
    setPets(prev => prev.filter(p => p.id !== petId));
    if (selected?.id === petId) setSelected(null);
  }, [profile?.id, selected?.id]);

  return (
    <div className="page-container">
      {/* Header */}
      {!selected && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="page-title">Astro Pet</h1>
          <p className="page-subtitle">
            Descubra a personalidade astrológica do seu pet e a sintonia entre vocês
          </p>
        </div>
      )}

      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {/* Selected pet detail */}
      {selected && !adding && (
        <PetDetail
          pet={selected}
          compat={compats[selected.id] ?? null}
          onBack={() => setSelected(null)}
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
      )}

      {/* Add form */}
      {!selected && adding && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1rem' }}>
            Adicionar pet
          </h2>
          <AddPetForm
            onSubmit={handleSubmit}
            submitting={submitting}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {/* Pet list */}
      {!selected && !adding && (
        <>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} style={{ borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', padding: '1rem', background: 'var(--surface-card)' }}>
                  <TextSkeleton lines={2} />
                </div>
              ))}
            </div>
          ) : pets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🐾</div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                Você ainda não tem pets cadastrados.<br />
                Adicione seu primeiro pet para ver seu mapa natal!
              </p>
              <button className="btn btn-primary" onClick={() => setAdding(true)} style={{ gap: '0.4rem' }}>
                <Plus size={18} /> Adicionar meu primeiro pet
              </button>
            </div>
          ) : (
            <>
              {pets.map(pet => (
                <PetCard
                  key={pet.id}
                  pet={pet}
                  compat={compats[pet.id] ?? null}
                  onClick={() => setSelected(pet)}
                  onDelete={() => handleDelete(pet.id)}
                />
              ))}
              <button
                className="btn btn-secondary"
                onClick={() => setAdding(true)}
                style={{ width: '100%', marginTop: '0.5rem', gap: '0.4rem', justifyContent: 'center' }}
              >
                <Plus size={16} /> Adicionar outro pet
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
